<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('price.pricesLists') }}</span>
        <h3 class="page-title">
          <template v-if="$route.meta.type === 'products'">{{ $t('price.productsPricesList') }}</template>
          <template v-if="$route.meta.type === 'services'">{{ $t('price.servicesPricesList') }}</template>
          <template v-if="$route.meta.type === 'setups'">{{ $t('price.setupPricesList') }}</template>
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          v-if="$route.meta.type === 'services' || $route.meta.type === 'products'"
          @click.prevent="openImportPopup"
          theme="primary"
          class="mr-2">
          <i class="material-icons">
            cloud_upload
          </i>
          {{ $t('price.importPrices') }}
        </d-button>

        <d-button
          v-if="$route.meta.type === 'services' || $route.meta.type === 'products'"
          @click.prevent="openExportPopup"
          theme="secondary"
          class="mr-2">
          <i class="material-icons">
            cloud_download
          </i>
          {{ $t('price.exportPrices') }}
        </d-button>

        <d-button
          v-if="$route.meta.type === 'products'"
          theme="success"
          @click.prevent="$router.push('/cenniki/produkty/nowy')">
          <i class="material-icons">add</i>
          {{ $t('price.addNewProductPrice') }}
        </d-button>
        <d-button
          v-if="$route.meta.type === 'services'"
          theme="success"
          @click.prevent="$router.push('/cenniki/zdobienia/nowy')">
          <i class="material-icons">add</i>
          {{ $t('price.addNewServicePrice') }}
        </d-button>
        <d-button
          v-if="$route.meta.type === 'setups'"
          theme="success"
          @click.prevent="$router.push('/cenniki/setup/nowy')">
          <i class="material-icons">add</i>
          {{ $t('price.addNewSetupPrice') }}
        </d-button>
      </d-col>
    </d-row>

    <list-filters
      class="mb-3"
      :config="filtersConfig"
      :isListLoaded="allDataLoaded"
      :view-name="'prices'"
      @init="handleFiltersInit"
      @search="handleFiltersSearch" />

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0">
              <template v-if="$route.meta.type === 'products'">{{ $t('product.productCode') }}</template>
              <template v-if="$route.meta.type === 'services' || $route.meta.type === 'setups'">{{ $t('service.serviceCode') }}</template>
            </th>
            <th
              scope="col"
              class="border-0">{{ $t('ui.name') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('price.price') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('settings.currency.currency') }}</th>
            <th
              v-if="$route.meta.type === 'products'"
              scope="col"
              class="border-0">
              {{ $t('price.isSale') }}
            </th>
            <th
              v-if="$route.meta.type === 'services'"
              scope="col"
              class="border-0">
              {{ $t('price.quantityFrom') }}
            </th>
            <th
              v-if="$route.meta.type === 'setups'"
              scope="col"
              class="border-0">
              {{ $t('price.quantityTo') }}
            </th>
            <th
              scope="col"
              class="border-0">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="allDataLoaded">
          <tr
            v-for="(price, index) of prices"
            :key="'country-' + index">
            <td class="align-middle">
              <template v-if="$route.meta.type === 'products'">
                <template v-if="price.product">
                  {{ price.product.code }}
                </template>
              </template>
              <template v-if="$route.meta.type === 'services' || $route.meta.type === 'setups'">
                <template v-if="price.service">
                  {{ price.service.code }}
                </template>
              </template>
            </td>
            <td class="align-middle">
              <template v-if="$route.meta.type === 'products'">
                <template v-if="price.product">
                  {{ price.product.name }}
                </template>
              </template>
              <template v-if="$route.meta.type === 'services' || $route.meta.type === 'setups'">
                <template v-if="price.service">
                  {{ price.service.name }}
                </template>
              </template>
            </td>
            <td class="align-middle">
              {{ Number(price.price).toFixed(2) }}
            </td>
            <td class="align-middle">
              <template v-if="price.currency">
                {{ price.currency.code }}
              </template>
            </td>
            <td
              v-if="$route.meta.type === 'products'"
              class="align-middle">
              <span
                v-if="price.is_sale"
                class="text-success">
                {{ $t('ui.yes') }}
              </span>
              <span
                v-else
                class="text-light">
                {{ $t('ui.no') }}
              </span>
            </td>
            <td
              v-if="$route.meta.type === 'services' || $route.meta.type === 'setups'"
              class="align-middle">
              {{ price.value_from }}
            </td>
            <td
              class="align-middle"
              style="width: 150px;">
              <d-button
                theme="primary"
                @click.native="$router.push(editUrl + price.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removePrice(price.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="prices.length === 0">
            <td
              colspan="6"
              class="text-center">
              {{ $t('price.noPricesToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a
          href="javascript:window.location.reload();"
        class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />

    <prices-export-popup
      v-if="showExportPopup"
      :currencies="currencies" />

    <prices-import-popup
      v-if="showImportPopup"
      :currencies="currencies" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';
import PricesExportPopup from './../components/prices/PricesExportPopup.vue';
import PricesImportPopup from './../components/prices/PricesImportPopup.vue';
import cloneDeep from 'lodash.clonedeep';

export default {
  name: 'prices',
  mixins: [
    HasPagination
  ],
  components: {
    'pagination': Pagination,
    'prices-export-popup': PricesExportPopup,
    'prices-import-popup': PricesImportPopup
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedCurrencies;
    },
    currencyOptions () {
      return this.currencies.map(currency => ({
        value: currency.id,
        text: currency.code
      }));
    },
    editUrl () {
      if (this.$route.meta.type === 'products') {
        return '/cenniki/produkty/edycja/';
      }

      if (this.$route.meta.type === 'services') {
        return '/cenniki/zdobienia/edycja/';
      }

      if (this.$route.meta.type === 'setups') {
        return '/cenniki/setup/edycja/';
      }

      return '';
    },
    endpointBase () {
      if (this.$route.meta.type === 'products') {
        return '/products/prices/';
      }

      if (this.$route.meta.type === 'services' || this.$route.meta.type === 'setups') {
        return '/services/prices/';
      }

      return '';
    },
    filtersConfig () {
      return [
        {
          key: 'search',
          apiKey: 'search',
          label: this.$t('ui.search'),
          placeholder: this.$t('product.enterProductNameOrCode'),
          type: 'text',
          isBasic: true,
          defaultValue: ''
        },
        {
          key: 'currency',
          apiKey: 'currency_id',
          label: this.$t('settings.currency.currency'),
          placeholder: this.$t('settings.currency.currency'),
          type: 'select',
          isBasic: true,
          defaultValue: null,
          options: this.currencyOptions
        }
      ];
    }
  },
  data () {
    return {
      prices: [],
      currencies: [],
      loaded: false,
      loadError: false,
      loadedCurrencies: false,
      showExportPopup: false,
      showImportPopup: false,
      removeInProgress: false,
      activeFilters: {},
      apiQueryPayload: {}
    };
  },
  mounted () {
    this.loadAdditionalData();
    this.$bus.$on('prices-export-popup-close', this.closeExportPopup);
    this.$bus.$on('prices-import-popup-close', this.closeImportPopup);
  },
  methods: {
    handleFiltersInit (filters, apiPayload) {
      this.activeFilters = filters;
      this.apiQueryPayload = apiPayload;

      this.loadFilteredData();
    },
    handleFiltersSearch (activeFilters, apiPayload) {
      this.activeFilters = activeFilters;
      this.apiQueryPayload = apiPayload;
      this.$bus.$emit('pagination-reset');
      this.debouncedLoadFilteredData();
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = cloneDeep(this.apiQueryPayload) || {};

      if (this.$route.meta.type === 'services') {
        where.is_setup = 0;
      }

      if (this.$route.meta.type === 'setups') {
        where.is_setup = 1;
      }

      if (this.$route.meta.type === 'products') {
        where.product_is_indent = 0;
      }

      ListUtils.loadPaginatedItemsData(this, {
        method: 'get',
        endpoint: this.endpointBase + 'items?page=' + this.currentPage,
        listField: 'prices',
        params: {
          where
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError'
      });
    },
    removePrice (priceID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: this.endpointBase + 'delete/',
        id: priceID,
        successTitle: this.$t('price.deletePriceSuccessTitle'),
        successText: this.$t('price.deletePriceSuccessText'),
        confirmTitle: this.$t('price.deletePriceConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('price.deletePriceErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    openExportPopup () {
      this.showExportPopup = true;
    },
    closeExportPopup () {
      this.showExportPopup = false;
    },
    openImportPopup () {
      this.showImportPopup = true;
    },
    closeImportPopup () {
      this.showImportPopup = false;
    }
  },
  beforeDestroy () {
    this.$bus.$off('prices-export-popup-close', this.closeExportPopup);
    this.$bus.$off('prices-import-popup-close', this.closeImportPopup);
  }
};
</script>
